import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Cropper } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import Resizer from 'react-image-file-resizer';
import { useIntl } from 'react-intl';

import ImageEditorSidebar from './Sidebar';
import ImageEditorSave from './Save';
import ImageEditorSidebarAILoader from './SidebarAILoader';

import { sendTrackingEvent } from '../../helpers';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { readDpiFromUrl, writeDpiToBlob } from '../../helpers/PreserveDPI';
import { getSetting } from '../../helpers/Settings';

const SAVED_PRESETS_KEY = 'saved-presets';

const ImageEditor = ({
  file,
  fileVersion,
  afterCreateVersion,
  afterCreateEdition,
  clientSettings,
  updateCommentsCount,
}) => {
  const intl = useIntl();

  const [initial] = useState(() => {
    const defaultCategory = clientSettings.image_editor_default_presets[0];
    const savedPresets = clientSettings.image_editor_presets || [];

    if (getSetting('image_editor_presets_show_by_default') && savedPresets.length > 0) {
      return {
        category: {
          label: intl.formatMessage({ defaultMessage: 'Saved presets' }),
          key: SAVED_PRESETS_KEY,
        },
        preset: savedPresets[0],
        mode: 'clear',
      };
    }

    return {
      category: defaultCategory,
      preset: defaultCategory.presets[0],
      mode: 'clear',
    };
  });

  const [mode, setMode] = useState(initial.mode);
  const [cropCategory, setCropCategory] = useState(initial.category);
  const [cropPreset, setCropPreset] = useState(initial.preset);
  const [cropDimensions, setCropDimensions] = useState({
    x: Number(initial.preset.x),
    y: Number(initial.preset.y),
  });
  const [rotation, setRotation] = useState(0);
  const [scaleX, setScaleX] = useState(1);
  const [scaleY, setScaleY] = useState(1);
  const [lockRatio, setLockRatio] = useState(true);
  const [fixedResolution, setFixedResolution] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [cropCategoryPresets, setCropCategoryPresets] = useState([]);
  const [boxHeight, setBoxHeight] = useState(null);
  const [shouldForcePNG, setShouldForcePNG] = useState(false);
  const [isExpandingMode, setIsExpandingMode] = useState(false);
  const [viewMode, setViewMode] = useState(1);
  const [cropperReady, setCropperReady] = useState(false);
  const [performingAIAction, setPerformingAIAction] = useState(false);
  const [AICollapseOpen, setAICollapseOpen] = useState(null);
  const [cropperData, setCropperData] = useState(null);

  const modeRef = useRef(initial.mode);

  const cropperRef = useRef(null);
  const lockRatioRef = useRef(lockRatio);
  const cropCategoryRef = useRef(cropCategory);
  const fixedResolutionRef = useRef(fixedResolution);
  const originalDpiRef = useRef(null);

  useEffect(() => {
    fixedResolutionRef.current = fixedResolution;
  }, [fixedResolution]);

  useEffect(() => {
    sendTrackingEvent('file-editor-open');
  }, []);

  useEffect(() => {
    cropCategoryRef.current = cropCategory;
  }, [cropCategory]);

  useEffect(() => {
    if (file?.urls?.original) {
      readDpiFromUrl(file.urls.original).then((dpi) => {
        originalDpiRef.current = dpi;
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cropRatioOptions = useMemo(() => ({
    default: [...clientSettings.image_editor_default_presets],
    clientSettings: [...clientSettings.image_editor_presets],
  }), [clientSettings]);

  const handleExpandingMode = useCallback((expandingMode) => {
    setIsExpandingMode(expandingMode);

    const cropper = cropperRef.current?.cropper;

    if (expandingMode) {
      setMode('expand');
      setViewMode(0);
    } else if (viewMode === 0) {
      cropper.clear();
      setMode('clear');
      setViewMode(1);
    }
  }, [viewMode]);

  const handleImageSrc = useCallback(() => {
    if (!file || !file.urls) return null;
    if (file.urls.converted) {
      setShouldForcePNG(true);
      setImageSrc(file.urls.converted);
    } else if (file.urls.original) {
      setImageSrc(file.urls.original);
    } else if (file.urls.preview) {
      setImageSrc(file.urls.preview);
    }

    return null;
  }, [file]);

  const isEnforcingRef = useRef(false);

  const enforceCropBoxContainsImage = () => {
    if (isEnforcingRef.current) return;

    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    const canvasData = cropper.getCanvasData();
    const cropBoxData = cropper.getCropBoxData();
    const containerData = cropper.getContainerData();

    let {
      left, top, width, height,
    } = cropBoxData;

    const canvasLeft = canvasData.left;
    const canvasTop = canvasData.top;
    const canvasRight = canvasData.left + canvasData.width;
    const canvasBottom = canvasData.top + canvasData.height;

    let cropRight = left + width;
    let cropBottom = top + height;

    if (left > canvasLeft) left = canvasLeft;
    if (top > canvasTop) top = canvasTop;
    if (cropRight < canvasRight) cropRight = canvasRight;
    if (cropBottom < canvasBottom) cropBottom = canvasBottom;

    width = cropRight - left;
    height = cropBottom - top;

    left = Math.max(0, Math.min(left, containerData.width - width));
    top = Math.max(0, Math.min(top, containerData.height - height));

    isEnforcingRef.current = true;
    cropper.setCropBoxData({
      left, top, width, height,
    });
    isEnforcingRef.current = false;

    const currentCropData = cropper.getData();

    setCropDimensions({
      x: Math.round(currentCropData.width),
      y: Math.round(currentCropData.height),
    });
  };

  const handleCropMove = () => {
    if (isEnforcingRef.current) return;

    if (cropperRef && cropperRef.current) {
      const { cropper } = cropperRef.current;
      const currentCropData = cropper.getData();
      const currentImageData = cropper.getImageData();

      setCropperData({
        data: currentCropData,
        imageData: currentImageData,
      });

      if (currentCropData && currentCropData.width && currentCropData.height) {
        if (isExpandingMode) {
          enforceCropBoxContainsImage();
        } else if (cropCategoryRef.current.key === 'custom' && !fixedResolutionRef.current) {
          const newX = Math.round(currentCropData.width);
          const newY = Math.round(currentCropData.height);

          if (cropDimensions.x !== newX || cropDimensions.y !== newY) {
            setCropDimensions({
              x: Math.round(currentCropData.width),
              y: Math.round(currentCropData.height),
            });
          }
        }
      }
    }
  };

  const handleCropPreset = useCallback((preset) => {
    setCropPreset(preset);
    setAICollapseOpen(null);

    if (isExpandingMode) {
      handleExpandingMode(false);
    }

    if (cropperRef && cropperRef.current) {
      const { cropper } = cropperRef.current;
      if (mode === 'clear' || mode === 'expand') {
        setMode('crop');
        cropper.crop();
      }
      if (preset) {
        const x = Number(preset.x);
        const y = Number(preset.y);
        cropper.setAspectRatio(x / y);
        setCropDimensions({ x, y });
        setLockRatio(true);
      }
    }
  }, [mode, handleExpandingMode, isExpandingMode]);

  const handleCropCategory = (category) => {
    setCropCategory(category);
    setAICollapseOpen(null);

    if (isExpandingMode) {
      handleExpandingMode(false);
    }

    if (category.key === 'custom') {
      setLockRatio(false);
      if (cropperRef && cropperRef.current) {
        const { cropper } = cropperRef.current;
        cropper.crop();
        const currentCropData = cropper.getData();

        setCropDimensions({
          x: Math.round(currentCropData.width),
          y: Math.round(currentCropData.height),
        });

        cropper.setAspectRatio(NaN);
      }
    } else {
      setLockRatio(true);
    }
  };

  const handleRotate = (degrees, rotationMode) => {
    if (cropperRef && cropperRef.current) {
      const { cropper } = cropperRef.current;
      if (rotationMode === 'absolute') {
        cropper.rotateTo(degrees);
        setRotation(Number(degrees));
      } else {
        cropper.rotate(Number(degrees));
        setRotation(Number(rotation + degrees));
      }

      sendTrackingEvent('file-editor-rotate', { rotation: degrees, mode: rotationMode });
    }
  };

  const handleFlip = (type) => {
    if (cropperRef && cropperRef.current) {
      const { cropper } = cropperRef.current;
      if (type === 'horizontal') {
        cropper.scaleX(scaleX * -1);
        setScaleX(scaleX * -1);
      } else if (type === 'vertical') {
        cropper.scaleY(scaleY * -1);
        setScaleY(scaleY * -1);
      }

      sendTrackingEvent('file-editor-flip', { type });
    }
  };

  const handleCropperInit = () => {
    if (cropperRef && cropperRef.current) {
      const { cropper } = cropperRef.current;
      if (lockRatioRef.current) {
        cropper.setAspectRatio(cropPreset.x / cropPreset.y);
      } else {
        cropper.setAspectRatio(NaN);
      }
    }
  };

  const handleCropperSwitch = (e) => {
    const { checked } = e.target;

    if (cropperRef && cropperRef.current) {
      const { cropper } = cropperRef.current;

      setAICollapseOpen(null);
      if (isExpandingMode) {
        handleExpandingMode(false);
      }

      if (checked) {
        cropper.crop();
        setMode('crop');
      } else {
        cropper.clear();
        setMode('clear');
      }

      sendTrackingEvent('file-editor-cropper-switch', { mode: checked ? 'crop' : 'clear' });
    }
  };

  const handleCropperDimensionsChange = (dimension, type) => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    let newWidth = cropDimensions.x;
    let newHeight = cropDimensions.y;

    if (fixedResolutionRef.current) {
      cropper.setAspectRatio(NaN);
    }

    if (type === 'width') {
      newWidth = dimension;

      if (lockRatio && !fixedResolutionRef.current) {
        const ratio = cropDimensions.x / cropDimensions.y;
        newHeight = dimension / ratio;
      }
    }

    if (type === 'height') {
      newHeight = dimension;

      if (lockRatio && !fixedResolutionRef.current) {
        const ratio = cropDimensions.x / cropDimensions.y;
        newWidth = dimension * ratio;
      }
    }

    cropper.setData({
      width: newWidth,
      height: newHeight,
    });

    if (fixedResolutionRef.current) {
      cropper.setAspectRatio(newWidth / newHeight);
    }

    setCropDimensions({
      x: Math.round(newWidth),
      y: Math.round(newHeight),
    });
  };

  const handleLockRatio = (value) => {
    setLockRatio(value);

    if (!value) {
      setFixedResolution(false);
    }

    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    const cropBox = cropper.getCropBoxData();
    const data = cropper.getData();

    if (value) {
      const ratio = data.width / data.height;
      cropper.setAspectRatio(ratio);
    } else {
      cropper.setAspectRatio(NaN);
    }

    cropper.setCropBoxData(cropBox);

    setCropDimensions({
      x: Math.round(data.width),
      y: Math.round(data.height),
    });

    sendTrackingEvent('file-editor-lock-ratio', { locked: value });
  };

  const handleFixedResolution = (value) => {
    setFixedResolution(value);
    handleLockRatio(value);

    sendTrackingEvent('file-editor-fixed-resolution', { fixed: value });
  };

  const getEditedFileName = () => {
    if (shouldForcePNG) {
      let name = file.original_name;

      const match = ['.jpg', '.jpeg'].find(end => name.endsWith(end));
      if (match) {
        name = name.slice(0, -match.length);
      }

      name += '.png';

      return name;
    }

    return file.original_name;
  };

  const getEditedFileType = () => {
    if (shouldForcePNG) return 'image/png';

    return file.type;
  };

  const getEditedFile = () => new Promise((resolve, reject) => {
    if (!cropperRef?.current) {
      reject(new Error(intl.formatMessage({ defaultMessage: 'Cropper reference not available' })));
      return;
    }

    const { cropper } = cropperRef.current;

    const croppedCanvas = mode === 'clear'
      ? cropper.getCroppedCanvas({
        width: cropper.getImageData().naturalWidth,
        height: cropper.getImageData().naturalHeight,
      })
      : cropper.getCroppedCanvas();

    const outputType = shouldForcePNG ? 'image/png' : file.type;

    const imageData = cropper.getImageData();

    const meta = {
      width: imageData.naturalWidth,
      height: imageData.naturalHeight,
    };

    if (mode !== 'clear') {
      if (cropPreset) {
        meta.width = cropPreset.x;
        meta.height = cropPreset.y;
      } else if (cropCategory.key === 'custom') {
        meta.width = cropDimensions.x;
        meta.height = cropDimensions.y;
      }
    }

    let fileName = file.original_name;

    if (shouldForcePNG && !file.original_name.toLowerCase().endsWith('.png')) {
      const nameWithoutExt = file.original_name.replace(/\.[^/.]+$/, '');
      fileName = `${nameWithoutExt}.png`;
    }

    croppedCanvas.toBlob(async (blob) => {
      try {
        const originalDpi = originalDpiRef.current;

        const applyDpi = async (targetBlob) => {
          if (!originalDpi) {
            return targetBlob;
          }
          const result = await writeDpiToBlob(targetBlob, originalDpi, outputType);
          return result;
        };

        const needsResize = (
          mode !== 'clear' &&
          mode !== 'expand' &&
          (cropPreset || cropCategory.key === 'custom')
        );

        if (needsResize) {
          const editedFile = new File([blob], fileName, { type: outputType });

          const resizedBlob = await new Promise((innerResolve, innerReject) => {
            try {
              Resizer.imageFileResizer(
                editedFile,
                meta.width,
                meta.height,
                outputType === 'image/jpeg' ? 'JPEG' : 'PNG',
                100,
                0,
                (resized) => innerResolve(resized),
                'blob',
                meta.width,
                meta.height,
                false,
              );
            } catch (e) {
              innerReject(e);
            }
          });

          const dpiBlob = await applyDpi(resizedBlob);
          const resizedFile = new File([dpiBlob], fileName, { type: outputType });
          resolve({ editedFile: resizedFile, meta });
        } else {
          const dpiBlob = await applyDpi(blob);
          const finalFile = new File([dpiBlob], fileName, { type: outputType });
          resolve({ editedFile: finalFile, meta });
        }
      } catch (error) {
        reject(error);
      }
    }, outputType);
  });

  const getFileToPerformAIAction = async () => {
    if (!imageSrc) return null;

    const response = await fetch(imageSrc);
    const blob = await response.blob();
    return new File([blob], getEditedFileName(), { type: getEditedFileType() });
  };

  useEffect(() => {
    handleImageSrc();
  }, [handleImageSrc]);

  useEffect(() => {
    const option = cropRatioOptions.default.find(o => o.key === cropCategory.key);
    if (option) {
      setCropCategoryPresets(option.presets);
      if (!option.presets.includes(cropPreset)) handleCropPreset(option.presets[0]);
    } else if (cropCategory.key === 'saved-presets') {
      setCropCategoryPresets(cropRatioOptions.clientSettings);
      if (!cropRatioOptions.clientSettings.includes(cropPreset)) {
        handleCropPreset(cropRatioOptions.clientSettings[0]);
      }
    } else {
      setCropCategoryPresets([]);
      handleCropPreset(null);
    }
  }, [cropCategory, cropPreset, cropRatioOptions, handleCropPreset]);

  const initialAspectRatio = () => {
    const preset = clientSettings.image_editor_default_presets[0].presets[0];
    return preset.x / preset.y;
  };

  const elRef = useCallback(node => {
    if (node !== null) {
      setBoxHeight(node.clientHeight);
    }
  }, []);

  const handleChangeSrc = (src, message) => {
    if (message) {
      addFlashMessage({
        type: 'success',
        text: message,
      });
    }

    setShouldForcePNG(true);
    setImageSrc(src);
  };

  const handleResetImage = () => {
    handleImageSrc();

    sendTrackingEvent('file-editor-reset-image');
  };

  const getExpandParameters = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return null;

    const cropData = cropper.getData();
    const imageData = cropper.getImageData();

    // Final canvas size (natural pixels)
    const canvasWidth = Math.round(cropData.width);
    const canvasHeight = Math.round(cropData.height);

    // Original image natural size
    const originalWidth = imageData.naturalWidth;
    const originalHeight = imageData.naturalHeight;

    // Position of image inside final canvas
    const offsetX = Math.round(-cropData.x);
    const offsetY = Math.round(-cropData.y);

    if (canvasWidth * canvasHeight > 25_000_000) {
      throw new Error('Canvas exceeds 25M pixel limit');
    }

    return {
      canvas_size: [canvasWidth, canvasHeight],
      original_image_size: [originalWidth, originalHeight],
      original_image_location: [offsetX, offsetY],
    };
  };

  const onCropperReady = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      if (modeRef.current === 'expand') {
        setLockRatio(false);

        cropper.crop();

        cropper.setAspectRatio(NaN);

        cropper.clear();
        cropper.crop();
        cropper.zoom(-0.95);
        const canvasData = cropper.getCanvasData();

        cropper.setCropBoxData({
          left: canvasData.left,
          top: canvasData.top,
          width: canvasData.width,
          height: canvasData.height,
        });

        return;
      }

      if (modeRef.current === 'clear') {
        cropper.clear();
      } else if (modeRef.current === 'crop') {
        cropper.crop();
      }

      setCropperReady(true);
    }
  };

  const callbackAIAction = (actionName, type, params) => {
    if (actionName === 'expand') {
      if (type === 'main') {
        handleExpandingMode(params.expandingMode);
      }

      if (type === 'getData') {
        return getExpandParameters();
      }
    } else if (isExpandingMode) {
      handleExpandingMode(false);
    }

    if (mode === 'crop' && actionName !== 'expand') {
      const cropper = cropperRef.current?.cropper;

      cropper.clear();
      setMode('clear');
    }

    return null;
  };

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    lockRatioRef.current = lockRatio;
  }, [lockRatio]);

  if (!imageSrc) {
    return <p>{intl.formatMessage({ defaultMessage: 'An error occured while retrieving the file.' })}</p>;
  }

  return (
    <div className="image-editor" ref={elRef}>
      <div className="image-editor-container">
        <div className="image-editor-box">
          {boxHeight && (
          <Cropper
            key={viewMode}
            style={{ width: '100%', height: boxHeight }}
            src={imageSrc}
            guides={false}
            ref={cropperRef}
            checkCrossOrigin={false}
            crossOrigin="anonymous"
            onInitialized={() => {
              handleCropperInit();
            }}
            viewMode={viewMode}
            dragMode="move"
            responsive={true}
            initialAspectRatio={initialAspectRatio()}
            ready={onCropperReady}
            crop={handleCropMove}
          />
          )}
        </div>
        <div className="image-editor-sidebar-wrapper">
          <div className="image-editor-sidebar">
            {cropperReady && (
            <>
              <ImageEditorSidebar
                src={imageSrc}
                mode={mode}
                rotation={rotation}
                handleRotate={handleRotate}
                handleFlip={handleFlip}
                lockRatio={lockRatio}
                handleLockRatio={handleLockRatio}
                cropDimensions={cropDimensions}
                cropRatioOptions={cropRatioOptions}
                handleCropperDimensionsChange={handleCropperDimensionsChange}
                handleCropperSwitch={handleCropperSwitch}
                cropCategory={cropCategory}
                handleCropCategory={handleCropCategory}
                cropPreset={cropPreset}
                handleCropPreset={handleCropPreset}
                cropCategoryPresets={cropCategoryPresets}
                getFileToPerformAIAction={getFileToPerformAIAction}
                handleChangeSrc={handleChangeSrc}
                callbackAIAction={callbackAIAction}
                isExpandingMode={isExpandingMode}
                cropperData={cropperData}
                performingAIAction={performingAIAction}
                handlePerformingAIAction={setPerformingAIAction}
                fixedResolution={fixedResolution}
                handleFixedResolution={handleFixedResolution}
                handleExpandingMode={handleExpandingMode}
                AICollapseOpen={AICollapseOpen}
                handleAICollapseOpen={setAICollapseOpen}
                handleResetImage={handleResetImage}
              />
              <ImageEditorSave
                getEditedFile={getEditedFile}
                file={file}
                fileVersion={fileVersion}
                cropDimensions={cropDimensions}
                afterCreateVersion={afterCreateVersion}
                afterCreateEdition={afterCreateEdition}
                updateCommentsCount={updateCommentsCount}
                shouldForcePNG={shouldForcePNG}
              />
            </>
            )}
          </div>
          <ImageEditorSidebarAILoader
            show={performingAIAction}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  clientSettings: state.App.settings,
});

ImageEditor.propTypes = {
  file: PropTypes.object.isRequired,
  fileVersion: PropTypes.number,
  clientSettings: PropTypes.object.isRequired,
  afterCreateVersion: PropTypes.func.isRequired,
  afterCreateEdition: PropTypes.func.isRequired,
  updateCommentsCount: PropTypes.func.isRequired,
};

ImageEditor.defaultProps = {
  fileVersion: null,
};

export default connect(mapStateToProps)(ImageEditor);
